import { CSSProperties, useEffect, useMemo, useState } from "react";

import { Button } from "@blueprintjs/core";
import debounce from "lodash/debounce";
import { useMediaQuery } from "react-responsive";
import { useNavigate } from "react-router";

import { useCurrentUser } from "@/components/helpers/custom-hooks/use-current-user";
import { useWorkspaceSlug } from "@/components/helpers/custom-hooks/use-workspace-slug";
import { Flex } from "@/components/layout/flexbox";
import { Media } from "@/components/layout/Media";
import { SearchBox } from "@/components/pages_logged_in/workspace-admin/SearchBox";
import { Filter } from "@/components/ui/filter";
import {
  QueryPaginatedRoleplaysOrderBy,
  QueryScenariosOrderBy,
  QueryScenariosStatus,
  useTagsQuery,
  VisibilityScope,
} from "@/graphql";
import { ScenariosPageType } from "@/types/roleplays";

import { CreateCollectionDialog } from "./CreateCollectionDialog";
import { useRoleplayListQuery } from "./hooks";
import { buildCreateScenarioUrl } from "./utils";

const collectionOrderByOptions = [
  {
    label: "Default Sort",
    selectedLabel: "Order",
    value: QueryPaginatedRoleplaysOrderBy.Default,
  },
  {
    label: "Creation Date (Descending)",
    value: QueryPaginatedRoleplaysOrderBy.CreatedAtDesc,
  },
  {
    label: "Creation Date (Ascending)",
    value: QueryPaginatedRoleplaysOrderBy.CreatedAtAsc,
  },
  { label: "Alphabetical", value: QueryPaginatedRoleplaysOrderBy.NameAsc },
];

const scenarioOrderByOptions = [
  {
    label: "Default Sort",
    selectedLabel: "Order",
    value: QueryScenariosOrderBy.Default,
  },
  {
    label: "Creation Date (Descending)",
    value: QueryScenariosOrderBy.CreatedAtDesc,
  },
  {
    label: "Creation Date (Ascending)",
    value: QueryScenariosOrderBy.CreatedAtAsc,
  },
  {
    label: "Order (Ascending)",
    value: QueryScenariosOrderBy.OrderAsc,
  },
  {
    label: "Order (Descending)",
    value: QueryScenariosOrderBy.OrderDesc,
  },
  { label: "Alphabetical", value: QueryScenariosOrderBy.NameAsc },
];

interface RoleplayListHeaderProps {
  selectedScopes: VisibilityScope[];
  selectedOwner: string | null;
  selectedTags: string[];
  selectedScenarioOrder?: QueryScenariosOrderBy;
  selectedCollectionOrder?: QueryPaginatedRoleplaysOrderBy;
  onChangeScope: (scopes: VisibilityScope[]) => void;
  onOwnerChange: (owner: string | null) => void;
  onSearch: (query: string) => void;
  style?: CSSProperties;
  loading?: boolean;
  onCreateCollectionFinished?: () => void;
  allowAdd?: boolean;
  onScenarioOrderChange?: (order: QueryScenariosOrderBy) => void;
  onCollectionOrderChange?: (order: QueryPaginatedRoleplaysOrderBy) => void;
  onTagsChange: (tagIds: string[]) => void;
  filterFor?: "collection" | "scenario";
  onCollectionsChange?: (collectionIds: string[]) => void;
  title?: string;
  selectedCollections?: string[];
  selectedStatus?: QueryScenariosStatus;
  showPracticeStatusFilter?: boolean;
  hasPracticed?: boolean | null;
  onStatusChange?: (status: QueryScenariosStatus) => void;
  showStatusFilter?: boolean;
  includeOrphaned?: boolean;
  onViewAllCollections?: (isViewAll: boolean) => void;
  pageType?: ScenariosPageType;
  onHasPracticedChange?: (hasPracticed: boolean | null) => void;
}

export function RoleplayListHeader({
  onChangeScope,
  onSearch,
  loading = false,
  onCreateCollectionFinished,
  allowAdd = false,
  onOwnerChange,
  selectedScopes,
  selectedOwner,
  selectedStatus,
  selectedTags,
  selectedScenarioOrder = QueryScenariosOrderBy.Default,
  selectedCollectionOrder = QueryPaginatedRoleplaysOrderBy.Default,
  showPracticeStatusFilter = false,
  hasPracticed,
  onScenarioOrderChange,
  onCollectionOrderChange,
  onTagsChange,
  filterFor = "collection",
  onCollectionsChange,
  title,
  selectedCollections,
  onStatusChange,
  showStatusFilter = false,
  includeOrphaned = true,
  onViewAllCollections,
  pageType,
  onHasPracticedChange,
}: RoleplayListHeaderProps) {
  const user = useCurrentUser();
  const navigate = useNavigate();
  const workspaceSlug = useWorkspaceSlug();

  const [isOpenCreateDialog, setIsOpenDialog] = useState(false);
  const scopeOptions = [
    { value: VisibilityScope.User, label: "Personal" },
    { value: VisibilityScope.Workspace, label: "Workspace" },
    { value: VisibilityScope.Global, label: "Global" },
  ].filter(
    (scope) => workspaceSlug || scope.value !== VisibilityScope.Workspace,
  );

  const ownerOptions = [
    { value: null, label: "Any Owner" },
    { value: user?.id ?? "", label: "Owned By Me" },
  ];

  const statusFilterOptions = [
    {
      label: "All",
      selectedLabel: "Status",
      value: QueryScenariosStatus.All,
    },
    {
      label: "Incomplete",
      value: QueryScenariosStatus.Incomplete,
    },
    {
      label: "Complete",
      value: QueryScenariosStatus.Complete,
    },
  ];

  const practiceStatusFilterOptions = [
    {
      label: "All",
      selectedLabel: "Practice Status",
      value: "all",
    },
    {
      label: "Never Practiced",
      value: "never",
    },
    {
      label: "Practiced",
      value: "practiced",
    },
  ] as const;

  const [tagSearch, setTagSearch] = useState("");
  const { data, refetch } = useTagsQuery({
    fetchPolicy: "cache-and-network",
    variables: {
      query: tagSearch,
      page: 1,
      pageSize: 10,
    },
    skip: !user?.workspaceRole,
  });

  const tags = data?.tags?.data || [];

  const { roleplays = [] } = useRoleplayListQuery({
    workspaceOnly: false,
    pageSize: 1000,
  });

  // Create stable debounced functions
  const debouncedOnCollectionsChange = useMemo(
    () => debounce((value: string[]) => onCollectionsChange?.(value), 200),
    [onCollectionsChange],
  );

  const debouncedOnStatusChange = useMemo(
    () =>
      debounce((value: QueryScenariosStatus) => onStatusChange?.(value), 200),
    [onStatusChange],
  );

  const debouncedRefetch = useMemo(
    () => debounce((query: string) => refetch({ query }), 200),
    [refetch],
  );

  const debouncedOnTagsChange = useMemo(
    () => debounce((value: string[]) => onTagsChange(value), 200),
    [onTagsChange],
  );

  const debouncedOnChangeScope = useMemo(
    () => debounce((scopes: VisibilityScope[]) => onChangeScope(scopes), 200),
    [onChangeScope],
  );

  const debouncedOnOwnerChange = useMemo(
    () => debounce((id: string | null) => onOwnerChange(id), 200),
    [onOwnerChange],
  );

  const debouncedOnHasPracticedChange = useMemo(
    () =>
      debounce(
        (value: "practiced" | "never" | "all") =>
          value === "all"
            ? onHasPracticedChange?.(null)
            : onHasPracticedChange?.(value === "practiced"),
        200,
      ),
    [onHasPracticedChange],
  );

  const handleTagSearchChange = debounce((value: string) => {
    setTagSearch(value);
    debouncedRefetch(value);
  }, 200);

  useEffect(() => {
    if (
      filterFor === "scenario" &&
      roleplays?.length > 0 &&
      onCollectionsChange
    ) {
      onCollectionsChange(
        roleplays.map((roleplay) => roleplay?.id || "").concat("-1"),
      );
      onViewAllCollections?.(true);
    }
  }, [roleplays]);

  const isXs = useMediaQuery({ query: "(max-width: 576px)" });

  const filter = useMemo(() => {
    return (
      <>
        <Flex justifyContent="space-between" className="w-full gap-4">
          <div className="hide-scroll-bar flex gap-2 overflow-x-auto">
            {filterFor === "scenario" && (
              <>
                <Filter
                  variant="multiple"
                  values={selectedCollections}
                  label="Collections"
                  showViewAll
                  onViewAllChange={onViewAllCollections}
                  options={
                    includeOrphaned
                      ? [
                          {
                            value: "-1",
                            label: "Orphaned",
                          },
                          ...(roleplays?.map((roleplay) => ({
                            value: roleplay?.id ?? "",
                            label: roleplay?.name ?? "",
                          })) || []),
                        ]
                      : roleplays?.map((roleplay) => ({
                          value: roleplay?.id ?? "",
                          label: roleplay?.name ?? "",
                        }))
                  }
                  onChange={debouncedOnCollectionsChange}
                />
                {showStatusFilter && (
                  <Filter
                    variant="single"
                    value={selectedStatus ?? QueryScenariosStatus.All}
                    label="Status"
                    options={statusFilterOptions}
                    onChange={debouncedOnStatusChange}
                  />
                )}
                {showPracticeStatusFilter && (
                  <Filter
                    variant="single"
                    value={
                      hasPracticed === true
                        ? "practiced"
                        : hasPracticed === false
                          ? "never"
                          : "all"
                    }
                    label="Practice Status"
                    options={practiceStatusFilterOptions}
                    onChange={debouncedOnHasPracticedChange}
                  />
                )}
              </>
            )}
            {!!workspaceSlug && (
              <Filter
                variant="multiple"
                showSearch
                showViewAll={false}
                onSearchChange={handleTagSearchChange}
                label="Tags"
                values={selectedTags || []}
                options={tags.map((tag) => ({
                  value: tag?.id ?? "",
                  label: tag?.name ?? "",
                }))}
                onChange={onTagsChange}
                onClose={() => {
                  setTagSearch("");
                }}
              />
            )}
            <Filter
              variant="multiple"
              values={selectedScopes}
              showViewAll
              label="Visibility"
              options={scopeOptions}
              onChange={debouncedOnChangeScope}
            />
            {pageType !== "personal" && (
              <Filter
                variant="single"
                value={selectedOwner}
                label="Owner"
                options={ownerOptions}
                onChange={debouncedOnOwnerChange}
              />
            )}
          </div>
          {filterFor === "scenario" ? (
            <Filter
              variant="single"
              label="Order"
              options={scenarioOrderByOptions}
              onChange={(value) => onScenarioOrderChange?.(value)}
              value={selectedScenarioOrder}
            />
          ) : (
            <Filter
              variant="single"
              label="Order"
              options={collectionOrderByOptions}
              value={selectedCollectionOrder}
              onChange={(value) => onCollectionOrderChange?.(value)}
            />
          )}
        </Flex>
      </>
    );
  }, [
    selectedOwner,
    selectedScopes,
    ownerOptions,
    tags,
    scopeOptions,
    user,
    selectedCollections,
    selectedStatus,
    filterFor,
    roleplays,
    includeOrphaned,
    statusFilterOptions,
    workspaceSlug,
    debouncedOnCollectionsChange,
    debouncedOnStatusChange,
    handleTagSearchChange,
    debouncedOnTagsChange,
    debouncedOnChangeScope,
    debouncedOnOwnerChange,
    onScenarioOrderChange,
    onCollectionOrderChange,
  ]);

  return (
    <>
      <Media greaterThanOrEqual="lg">
        {title && <div className="text-2xl font-bold">{title}</div>}
        <Flex
          fontSize={24}
          fontWeight={600}
          mt={3}
          mb={3}
          justifyContent="space-between"
        >
          <Flex className="w-full gap-4" flexDirection="column">
            <Flex justifyContent="space-between" className="w-full">
              <SearchBox
                style={{
                  borderRadius: 6,
                  border: "0.5px solid #E2E2E2",
                  overflow: "hidden",
                  padding: "22px 0px 22px 36px",
                  fontSize: 16,
                }}
                className="icon-center"
                disabled={loading}
                refine={onSearch}
                throttledDuration={400}
                searchPlaceholder={"Search by name"}
              />
              {allowAdd && (
                <Button
                  intent="primary"
                  icon="plus"
                  style={{ fontSize: 15, marginLeft: 10 }}
                  onClick={() => {
                    if (filterFor === "scenario") {
                      navigate(buildCreateScenarioUrl());
                    } else {
                      setIsOpenDialog(true);
                    }
                  }}
                >
                  Create{" "}
                  {!isXs
                    ? filterFor === "scenario"
                      ? "Scenario"
                      : "Collection"
                    : ""}
                </Button>
              )}
            </Flex>
            {filter}
          </Flex>
        </Flex>
      </Media>

      <Media lessThan="lg">
        {title && <div className="text-xl font-bold">{title}</div>}
        <Flex className="gap-3" flexDirection="column" mt={3} mb={3}>
          <Flex
            fontSize={24}
            fontWeight={600}
            justifyContent="space-between"
            style={{ width: "100%" }}
            className="gap-2"
          >
            <SearchBox
              style={{
                borderRadius: 6,
                border: "0.5px solid #E2E2E2",
                overflow: "hidden",
                padding: "22px 0px 22px 36px",
                fontSize: 16,
                flex: 1,
              }}
              className="icon-center"
              disabled={loading}
              refine={onSearch}
              throttledDuration={400}
              searchPlaceholder="Search Collections..."
              containerClassName="flex-1"
            />

            {allowAdd && (
              <Button
                intent="primary"
                icon="plus"
                style={{ fontSize: 15 }}
                onClick={() => setIsOpenDialog(true)}
              >
                Create
              </Button>
            )}
          </Flex>
          {filter}
        </Flex>
      </Media>
      {filterFor === "collection" && (
        <CreateCollectionDialog
          isOpen={isOpenCreateDialog}
          onClose={() => setIsOpenDialog(false)}
          onFinished={onCreateCollectionFinished}
        />
      )}
    </>
  );
}
